import { AxiosResponse } from 'axios';
import { put, call, takeLatest, takeEvery, throttle ,delay} from 'redux-saga/effects';
import { ACTIVE_ORDERS } from '../../constants';

import {
  cartItemFailure,
  cartItemSuccess,
  failedPlaceOrder,
  getCustomerOffersFailure,
  getCustomerOffersSuccess,
  fetchCartRecommendationsFailure,
  fetchCartRecommendationsSuccess,
  recClickFailure,
  recClickSuccess,
  recConvertFailure,
  recConvertSuccess,
  successPlaceOrder,
  validateBogoItemSuccess,
  validateBogoItemFailure,
} from './cartActions';


import { CART_ITEM_REQUEST, PLACE_ORDER_REQUEST } from './cartConstants';
import {  getCustomerOffers } from './cartAPI';
import {  GET_CUSTOMER_OFFERS_REQUEST } from './cartConstants';
import { cartItem, placeDineInOrder, placeOnlineOrder, validateBogoItem, fetchCartRecommendationsApi, recClickApi, recConvertApi } from './cartAPI';
import {
  VALIDATE_BOGO_ITEM_REQUEST,
  CART_RECOMMENDATIONS_REQUEST,
  REC_CLICK_REQUEST,
  REC_CONVERT_REQUEST
} from './cartConstants';
import { getOrderDetails } from '../order/orderAPI';
import { POST_PAYMENT_LOYALTY_REQUEST } from './cartConstants';
import { Cart } from './cartModels';
import { successGetOrderDetail } from '../order/orderActions';
import { OrderDetail } from '../order/orderModels';
import { decryptJson } from '../../utils/react-ec-utils';
import { showErrorToast, showErrorToastDesktop } from '../../utils/toastUtils';
import { validateCoupon } from '../restaurant/restaurantAPI';
import { Store } from '../../configureStore';
import { trackEvent, ANALYTICS_EVENTS, normalizeOrderType } from '../../../utils/analytics';
import { validateCouponFailure, validateCouponSuccess } from '../restaurant/restaurantAction';
import { logPrint } from '../auth/AuthAPI';
// After payment success: fetch updated order detail for the receipt page.
import { validateAndParseResponse } from '../../utils/hmac-utils';
import { resolveOrderIdempotency, markOrderCreated } from '../../../helper/idempotencyKey.helper';

export function* dispatchLoyaltyRedeemAfterOrder(decryptedData: any, payloadDetails: any): Generator<any, void, any> {
  try {
    const orderId: string = decryptedData?.orderId || '';
    if (!orderId) return;

    // Fetch updated customer order detail post-payment.
    // Delay 1s so locations/slug (fired by OrderTracking on mount) lands first.
    try {
      yield delay(1000);
      const customerOrderResponse: AxiosResponse = yield call(getOrderDetails, { orderId });
      if (customerOrderResponse.status === 200) {
        const orderData = decryptJson(customerOrderResponse.data?.encryptedText);
        yield put(successGetOrderDetail(orderData));
      }
    } catch { /* non-blocking */ }

  } catch (err) {
    console.log('[loyalty-post-payment] error', err);
  }
}

const hasSignedResponseMetadata = (responseData: any) =>
  typeof responseData?.dataString === 'string' &&
  typeof responseData?.nonce === 'string' &&
  typeof responseData?.signature === 'string';

const getPlaceOrderSuccessPayload = async (responseData: any) => {
  if (responseData?.encryptedText) {
    return {
      status: 200,
      valid: true,
      data: decryptJson(responseData.encryptedText),
    };
  }

  if (hasSignedResponseMetadata(responseData)) {
    return validateAndParseResponse(responseData);
  }

  if (responseData?.data) {
    return {
      status: 200,
      valid: true,
      data: responseData.data,
    };
  }

  return {
    status: 400,
    valid: false,
    message: responseData?.message || 'Please Retry',
  };
};

const getPlaceOrderErrorMessage = (responseData: any, parsedResponse?: any) => {
  let parsedDataString;
  let decryptedErrorPayload;

  if (typeof responseData?.dataString === 'string') {
    try {
      parsedDataString = JSON.parse(responseData.dataString);
    } catch {
      parsedDataString = null;
    }
  }

  if (responseData?.encryptedText) {
    try {
      decryptedErrorPayload = decryptJson(responseData.encryptedText);
    } catch {
      decryptedErrorPayload = null;
    }
  }

  return (
    parsedResponse?.data?.message ||
    parsedResponse?.message ||
    responseData?.data?.message ||
    responseData?.message ||
    decryptedErrorPayload?.message ||
    parsedDataString?.message ||
    'Please Retry'
  );
};

const isValidPlaceOrderData = (responseData: any) =>
  Boolean(responseData?.orderId && responseData?.order);

function* handlePlaceOrderSuccess(action: any, callBackFunc: any, decryptedData: any): any {
  const printLog = {
    localStorage: action.payload?.allStorage,
    cookies: action.payload?.cookies,
    orderId: decryptedData.orderId,
  };

  yield call(logPrint, JSON.stringify(printLog));
  yield put(successPlaceOrder(decryptedData.orderId));
  yield put(successGetOrderDetail(decryptedData.order as OrderDetail));

  const activeOrder =
    localStorage.getItem('ACTIVE_ORDERS') &&
    JSON.parse(localStorage.getItem('ACTIVE_ORDERS') || '');

  if (!activeOrder) {
    const data = [
      {
        orderId: decryptedData.orderId,
        expiryTime: new Date().setHours(new Date().getHours() + 1),
      },
    ];
    localStorage.setItem(ACTIVE_ORDERS, JSON.stringify(data));
  } else {
    const data = [
      {
        orderId: decryptedData.orderId,
        expiryTime: new Date().setHours(new Date().getHours() + 1),
      },
      ...activeOrder,
    ];
    localStorage.setItem(ACTIVE_ORDERS, JSON.stringify(data));
  }

  if (callBackFunc != null) {
    callBackFunc(true, decryptedData);
  }
}

function* handlePlaceOrderFailure(
  responseData: any,
  parsedResponse: any,
  isMobile: boolean,
  callBackFunc?: any
): any {
  const errorMessage = getPlaceOrderErrorMessage(responseData, parsedResponse);
  // Order-total-mismatch is surfaced via the "cart updated" popup instead of a toast
  // (CheckOut.tsx), since it means the price/quantities shown to the customer are stale.
  if (!errorMessage?.includes('Order total mismatch')) {
    if (isMobile) {
      showErrorToast(errorMessage);
    } else {
      showErrorToastDesktop(errorMessage);
    }
  }
  const state: any = Store.getState();
  trackEvent(ANALYTICS_EVENTS.PAYMENT_FAILED, {
    order_type: normalizeOrderType(state?.cart?.currentOrderType),
    grand_total: state?.cart?.totalDetails?.orderTotals?.find((t: any) => t.code === '5')?.value,
  });
  yield put(failedPlaceOrder(errorMessage));
  if (callBackFunc != null) {
    callBackFunc(false);
  }
}

function* placeOrderSaga(action: any): any {
  const isMobile = window.innerWidth <= 768;
  let parsedResponse;
  let callBackFunc = null;
  try {
    let couponCode = '';

    if (
      action.payload?.details?.sagaCallBack != null &&
      typeof action.payload?.details?.sagaCallBack === 'function'
    ) {
      callBackFunc = action.payload?.details?.sagaCallBack;
      delete action.payload?.details?.sagaCallBack;
    }
    if (
      action.payload?.details?.couponCode != null &&
      typeof action.payload?.details?.couponCode == 'string'
    ) {
      couponCode = action.payload?.details?.couponCode;
      delete action.payload?.details?.couponCode;
    }
    const payload = {
      customerId: action?.payload?.details?.customerId,
      couponCode: couponCode,
      locationId: action?.payload?.details?.locationId,
      orderTotal: action?.payload?.details?.orderTotal,
    };

    const isDineInApi = action?.payload?.orderApiType === 'DINE_IN';
    const createOrderApi = isDineInApi ? placeDineInOrder : placeOnlineOrder;
    const orderDetailsPayload: any = {
      ...(action.payload.details as Cart),
    };

    if (isDineInApi) {
      if (!orderDetailsPayload.guestCount || Number(orderDetailsPayload.guestCount) < 1) {
        orderDetailsPayload.guestCount = 1;
      }
    }

    // Idempotency: reuse the key (and, if Create Order already succeeded for this
    // exact bill, the resulting order itself) when the bill hasn't changed since the
    // last attempt — covers retrying after a failed/offline payment without creating
    // a duplicate order. Any bill change (items, tip, address, offers, total, ...)
    // mints a fresh key for a fresh order — couponCode is passed in separately since
    // it was already stripped off orderDetailsPayload above.
    const { idempotencyKey, reusableOrder } = resolveOrderIdempotency(
      { ...orderDetailsPayload, couponCode },
      payload.customerId
    );
    orderDetailsPayload.idempotencyKey = idempotencyKey;

    if (reusableOrder) {
      yield* handlePlaceOrderSuccess(action, callBackFunc, reusableOrder.orderData);
      return;
    }

    if (couponCode !== '') {
      const ValidCouponresponse: AxiosResponse = yield call(validateCoupon, payload as any);

      if (ValidCouponresponse?.status === 200) {
        const decryptedDataCoupon = decryptJson(ValidCouponresponse.data.encryptedText);

        yield put(validateCouponSuccess(decryptedDataCoupon));
        if (decryptedDataCoupon?.status === 200) {
          const response: AxiosResponse = yield call(createOrderApi, orderDetailsPayload as Cart);
          parsedResponse = yield call(getPlaceOrderSuccessPayload, response.data);
          if (!parsedResponse?.valid) {
            yield* handlePlaceOrderFailure(response?.data, parsedResponse, isMobile, callBackFunc);
            return;
          }
          if (response.status === 200) {
            const decryptedData = parsedResponse?.data;
            if (!isValidPlaceOrderData(decryptedData)) {
              yield* handlePlaceOrderFailure(
                response?.data,
                parsedResponse,
                isMobile,
                callBackFunc
              );
              return;
            }
            markOrderCreated(idempotencyKey, decryptedData.orderId, decryptedData);
            yield* handlePlaceOrderSuccess(action, callBackFunc, decryptedData);
          } else {
            yield* handlePlaceOrderFailure(response?.data, parsedResponse, isMobile, callBackFunc);
          }
        }
      } else {
        yield put(validateCouponFailure(''));
        if (callBackFunc != null) {
          callBackFunc(false);
        }
      }
    } else {
      const response: AxiosResponse = yield call(createOrderApi, orderDetailsPayload as Cart);

      // Error bodies (unlike success bodies) aren't encrypted/signed, so they
      // never satisfy getPlaceOrderSuccessPayload's shape checks and would
      // otherwise fall into its generic `valid: false` branch below, which
      // short-circuits before this items-array case is ever reached. Handle
      // it against the raw response first, independent of the decrypt pipeline.
      
      
      if (response.status === 400) {
        const rawResponseData = response?.data;
        const unavailableItems = rawResponseData?.items ?? rawResponseData?.data?.items;
        const responseHttpStatus = rawResponseData?.httpStatus ?? rawResponseData?.data?.httpStatus;
        if (responseHttpStatus === 400 && unavailableItems?.length > 0) {
          const unavailableItemsData=unavailableItems?.map((item:any)=>({
            ...item,
            itemAvailble:false

          }))    
          

          yield put(failedPlaceOrder(unavailableItemsData));
          if(orderDetailsPayload?.items?.length===unavailableItemsData?.length)
          {
              if (isMobile) {
            showErrorToast(
              ' Some items in your cart are no longer available. Please remove them to continue.',
              'inactive-items'
            );
          } else {
            showErrorToastDesktop(
              'Some items in your cart are no longer available. Please remove them to continue.',
              'inactive-items'
            );
          }

          }
        
          if (callBackFunc != null) {
            callBackFunc(false);
          }
          return;
        }
      }

      parsedResponse = yield call(getPlaceOrderSuccessPayload, response.data);
      if (!parsedResponse?.valid) {
        yield* handlePlaceOrderFailure(response?.data, parsedResponse, isMobile, callBackFunc);
        return;
      }
      if (response.status === 200) {
        const decryptedData = parsedResponse?.data;
        if (!isValidPlaceOrderData(decryptedData)) {
          yield* handlePlaceOrderFailure(response?.data, parsedResponse, isMobile, callBackFunc);
          return;
        }
        markOrderCreated(idempotencyKey, decryptedData.orderId, decryptedData);
        yield* handlePlaceOrderSuccess(action, callBackFunc, decryptedData);
      } else {
        yield* handlePlaceOrderFailure(response?.data, parsedResponse, isMobile, callBackFunc);
      }
    }
  } catch (err: any) {
    const errorMessage = getPlaceOrderErrorMessage(err?.response?.data);
    if (isMobile) {
      showErrorToast(errorMessage);
    } else {
      showErrorToastDesktop(errorMessage);
    }
    yield put(failedPlaceOrder(errorMessage));
    if (callBackFunc != null) {
      callBackFunc(false);
    }
  }
}

function* cartItemSaga(action: any) {
  try {
    const response: AxiosResponse = yield call(cartItem, action.payload);
    if (response.status === 200) {
      yield put(cartItemSuccess(response?.data));
    } else {
      yield put(cartItemFailure(response?.data));
    }
  } catch (err: any) {
    yield put(cartItemFailure(err));
  }
}

function* getCustomerOffersSaga(action: any) {
  try {
    const response: AxiosResponse = yield call(getCustomerOffers, action.payload);
    if (response.status === 200) {
      const raw = response?.data?.offers ?? response?.data?.data ?? response?.data;
      yield put(getCustomerOffersSuccess(Array.isArray(raw) ? raw : []));
    } else {
      yield put(getCustomerOffersFailure(response?.data));
    }
  } catch (err: any) {
    yield put(getCustomerOffersFailure(err));
  }}



function* validateBogoItemSaga(action: any) {
  try {


    const response: AxiosResponse = yield call(validateBogoItem, action.payload);
    const responseData = {
      itemId: action.payload.itemId,
      httpStatus: response?.data?.httpStatus,
      message: response?.data?.message,
    };
    if (response.status === 200) {
      if (response?.data?.httpStatus === 409) {
        yield put(validateBogoItemFailure(responseData));
      } else {
        yield put(validateBogoItemSuccess({ itemId: action.payload.itemId }));
      }
    } else if (response.status === 409) {
      yield put(validateBogoItemFailure(responseData));
    } else {
      yield put(validateBogoItemFailure('failued'));
    }
  } catch (err: any) {
    yield put(validateBogoItemFailure(err));
  }
}
function* postPaymentLoyaltySaga(action: any) {
  const { orderId, locationId } = action.payload || {};
  yield* dispatchLoyaltyRedeemAfterOrder({ orderId }, { locationId });
}

function* cartRecommendationsSaga(action: any): any {
  try {
    const response: AxiosResponse = yield call(fetchCartRecommendationsApi, action.payload);
    if (response.status === 200) {
      yield put(fetchCartRecommendationsSuccess(response.data));
    } else {
      yield put(fetchCartRecommendationsFailure(response.data?.message || 'Failed to fetch recommendations'));
    }
  } catch (err: any) {
    yield put(fetchCartRecommendationsFailure(err?.message || 'Failed to fetch recommendations'));
  }
}

function* recConvertSaga(action: any): any {
  try {
    const response: AxiosResponse = yield call(recConvertApi, action.payload);
    if (response.status === 200 || response.status === 201) {
      yield put(recConvertSuccess(response.data));
    } else {
      yield put(recConvertFailure(response.data?.message || 'rec/convert failed'));
    }
  } catch (err: any) {
    yield put(recConvertFailure(err?.message || 'rec/convert failed'));
  }
}

function* recClickSaga(action: any): any {
  try {
    const response: AxiosResponse = yield call(recClickApi, action.payload);
    if (response.status === 200 || response.status === 201) {
      yield put(recClickSuccess(response.data));
    } else {
      yield put(recClickFailure(response.data?.message || 'rec/click failed'));
    }
  } catch (err: any) {
    yield put(recClickFailure(err?.message || 'rec/click failed'));
  }
}

export default function* cartSaga() {
  yield throttle(5000, PLACE_ORDER_REQUEST, placeOrderSaga);
  yield takeLatest(CART_ITEM_REQUEST, cartItemSaga);
  yield takeLatest(GET_CUSTOMER_OFFERS_REQUEST, getCustomerOffersSaga);
  yield takeEvery(VALIDATE_BOGO_ITEM_REQUEST, validateBogoItemSaga);
  yield takeLatest(POST_PAYMENT_LOYALTY_REQUEST, postPaymentLoyaltySaga);
  yield takeLatest(CART_RECOMMENDATIONS_REQUEST, cartRecommendationsSaga);
  yield takeEvery(REC_CLICK_REQUEST, recClickSaga);
  yield takeLatest(REC_CONVERT_REQUEST, recConvertSaga);
}
